import {useEffect} from 'react';
import {Routes, Route, Navigate, useNavigate} from 'react-router-dom';
import {useAuth} from './contexts/AuthContext';

// Auth Pages
import AuthPopupLayout from './pages/auth/AuthPopupLayout';
import PopupRouteGuard from './components/PopupRouteGuard';
import LoginForm from './pages/auth/LoginForm';
import RegistrationForm from './pages/auth/RegistrationForm';
import ForgotPassword from './pages/auth/ForgotPassword';
import SelectAccount from './pages/auth/SelectAccount';
import VerifyInterrupted from './pages/auth/VerifyInterrupted';
import AuthPopupNext from './pages/auth/AuthPopupNext'; // Import AuthPopupNext
// PortalMain was replaced by inline layout content; keep file if needed, not imported here

// Dashboard Layout & Pages
import DashboardLayout from './layouts/DashboardLayout';
import Overview from './pages/dashboard/Overview';
import ProfileManager from './pages/dashboard/ProfileManager';
import BankDetailsView from './pages/dashboard/BankDetailsView';
import DevConsole from './pages/dashboard/DevConsole';
import AppWorkspace from './pages/dashboard/AppWorkspace';
import ProjectView from './pages/dashboard/ProjectView';
import IntegrationSdkDocs from './pages/dashboard/IntegrationSdkDocs';

// Admin Pages
import SystemApprovals from './pages/admin/SystemApprovals';
import UserGovernance from './pages/admin/UserGovernance';

// Informational pages
import Help from './pages/info/Help';
import About from './pages/info/About';
import Contact from './pages/info/Contact';
import Home from './pages/Home.jsx';
import ScrollToTop from './components/ScrollToTop';

/**
 * Registers the React Router navigate() function into AuthContext so the
 * postMessage login handler can do client-side navigation without a full reload.
 * Must be rendered inside both BrowserRouter and AuthProvider.
 */
function NavigateRegistrar() {
    const navigate = useNavigate();
    const auth = useAuth();
    useEffect(() => {
        if (auth?.setNavigateCallback) {
            auth.setNavigateCallback(navigate);
        }
    }, [navigate, auth]);
    return null;
}

export default function App() {
    return (
        <>
            {/* Resets scroll position to top on navigation */}
            <ScrollToTop/>

            {/* Registers React Router navigate() into AuthContext for client-side post-login navigation */}
            <NavigateRegistrar/>

            <Routes>
                {/* Root now renders the unified Home / Onboarding Hub */}
                <Route path="/" element={<Home/>}/>

                {/* Isolated Zero-Redirect Popup Auth Frame (refactored to sub-routes) */}
                <Route path="/auth/popup" element={<PopupRouteGuard><AuthPopupLayout/></PopupRouteGuard>}>
                    {/* Default /auth/popup redirects directly to password login */}
                    <Route index element={<Navigate to="password" replace/>}/>
                    <Route path="password" element={<LoginForm/>}/>
                    <Route path="sso" element={<></>}/> {/* Renders the SSO/Social options inside AuthPopupLayout */}
                    <Route path="register" element={<RegistrationForm/>}/>
                    <Route path="forget" element={<ForgotPassword/>}/>
                    <Route path="verify-interrupted" element={<VerifyInterrupted/>}/>
                    <Route path="next" element={<AuthPopupNext/>}/> {/* Register AuthPopupNext route */}
                </Route>
                {/* Legacy /auth-popup route redirects to the full tabbed auth flow */}
                <Route path="/auth-popup" element={<Navigate to="/auth/popup" replace/>}/>
                <Route path="/auth/select-account" element={<SelectAccount/>}/>

                {/* Dashboard Routes with Layout Wrapper - account aware */}
                <Route path="/u/:accountIndex" element={<DashboardLayout/>}>
                    {/* Redirect bare /u/:accountIndex to overview */}
                    <Route index element={<Navigate to="dashboard/overview" replace/>}/>
                    <Route path="dashboard/overview" element={<Overview/>}/>
                    <Route path="dashboard/profile" element={<ProfileManager/>}/>
                    <Route path="dashboard/profile/bank/:id" element={<BankDetailsView/>}/>
                    <Route path="dashboard/console" element={<DevConsole/>}>
                        <Route index element={<Navigate to="workspace" replace/>}/>
                        <Route path="workspace" element={<AppWorkspace/>}/>
                        <Route path="workspace/:app_id" element={<ProjectView/>}/>
                        <Route path="docs" element={<IntegrationSdkDocs/>}/>
                    </Route>
                </Route>

                {/* Informational pages (prevent 404s from footer links) */}
                <Route path="/help" element={<Help/>}/>
                <Route path="/about" element={<About/>}/>
                <Route path="/contact" element={<Contact/>}/>

                {/* Convenience top-level dashboard shortcut -> account 0 overview */}
                <Route path="/dashboard" element={<Navigate to="/u/0/dashboard/overview" replace/>}/>

                {/* Admin routes rendered inside master DashboardLayout */}
                <Route path="/admin" element={<DashboardLayout/>}>
                    <Route path="approvals" element={<SystemApprovals/>}/>
                    <Route path="users" element={<UserGovernance/>}/>
                </Route>

                {/* Automatic Fallback Route -> Home */}
                <Route path="*" element={<Navigate to="/" replace/>}/>
            </Routes>
        </>
    );
}